import Image from 'next/image';
import { useEffect, useMemo, useState } from 'react';
import { useInView } from 'react-intersection-observer';
import styled from 'styled-components';

import poweredByImage from '../../../../assets/images/pb_tenor.png';
import theme from '../../../../assets/theme';
import { useMetrics } from '../../../../hooks/useMetrics';
import { TenorGIF, useInifiniteTenorGifs } from '../../../../hooks/useTenorGIFs';
import { Switch } from '../../../_atoms/_Switch';
import SearchInput from '../../../_molecules/SearchInput';

function splitArrayEveryOther<T>(arr: T[]): [T[], T[]] {
  const array1: T[] = [];
  const array2: T[] = [];

  arr.forEach((item, index) => {
    if (index % 2 === 0) {
      array1.push(item);
    } else {
      array2.push(item);
    }
  });

  return [array1, array2];
}

const TenorGIFPicker = ({
  onGifSelected,
}: {
  onGifSelected: (gif: TenorGIF) => void;
}) => {
  const metrics = useMetrics();
  const [searchTerm, setSearchTerm] = useState('');
  const [transparentSelected, setTransparentSelected] = useState(false);

  const { items, canFetchMore, isFetching, fetchMore } = useInifiniteTenorGifs(
    transparentSelected ? `transparent ${searchTerm}` : searchTerm,
  );

  const itemColumns = useMemo(() => splitArrayEveryOther(items || []), [items]);

  const { ref: scrollRef, inView } = useInView();
  useEffect(() => {
    if (inView && canFetchMore && !isFetching) {
      fetchMore();
    }
  }, [inView, canFetchMore, isFetching, fetchMore]);

  return (
    <CenteredDiv>
      <div style={{ padding: '0px 20px', marginBottom: 6 }}>
        <SearchInput
          value={searchTerm}
          placeholder={'Search GIFs'}
          onChange={setSearchTerm}
          style={{ width: '100%' }}
        />
      </div>

      <div
        style={{
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 20,
          marginBottom: 5,
        }}
      >
        <Image height={15} width={118} alt="Powered by Tenor" src={poweredByImage} />
        <div
          style={{
            height: '100%',
            display: 'flex',
            alignItems: 'center',
            cursor: 'pointer',
          }}
          onClick={() => {
            metrics.logEvent('Creator.Media.Tenor.Transparent.Clicked');
            setTransparentSelected(e => !e);
          }}
        >
          <Switch color={theme.colors.blue} value={transparentSelected} />{' '}
          <span style={{ marginLeft: '8px' }}>Transparent</span>
        </div>
      </div>

      <GIFGrid>
        <GIFColumn>
          {itemColumns[0].map(gif => (
            <GifItem
              key={gif.id}
              src={gif.media_formats.tinygif.url}
              alt={gif.title}
              onClick={() => onGifSelected(gif)}
            />
          ))}
        </GIFColumn>
        <GIFColumn>
          {itemColumns[1].map(gif => (
            <GifItem
              key={gif.id}
              src={gif.media_formats.tinygif.url}
              alt={gif.title}
              onClick={() => onGifSelected(gif)}
            />
          ))}
          <div ref={scrollRef} style={{ height: 1 }} />
        </GIFColumn>
      </GIFGrid>
    </CenteredDiv>
  );
};

const GIFColumn = styled.div`
  width: 100%;

  & > *:not(:first-child) {
    margin-top: 5px;
  }
  & > *:not(:last-child) {
    margin-bottom: 5px;
  }
`;

const GifItem = styled.img`
  border-radius: 5px;
  width: 100%;
  overflow: clip;
  cursor: pointer;
`;

const GIFGrid = styled.div`
  width: 100%;
  height: calc(100% - 72px);
  padding: 0px 20px;
  overflow-y: auto;
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 6px;
`;

const CenteredDiv = styled.div`
  width: 100%;
  height: 100%;
`;

export default TenorGIFPicker;
